// Main upload processor service that orchestrates file parsing and storage
import { supabase } from './supabase';
import { spotifyParserService } from './spotifyParser';
import { hypedditParserService } from './hypedditParser';
import { deduplicationService } from './deduplication';
import { noderr } from '../../shared/utils/errorHandler';
import type { 
  UploadRequest, 
  UploadResponse, 
  UploadProgress, 
  DataUpload,
  ParsedSpotifyData,
  ParsedHypedditData
} from '../../shared/types/analytics';

export class UploadProcessorService {
  private progressCallbacks: Map<string, (progress: UploadProgress) => void> = new Map();

  /**
   * Register a progress callback for upload tracking
   */
  onProgress(uploadId: string, callback: (progress: UploadProgress) => void): void {
    this.progressCallbacks.set(uploadId, callback);
  }

  /**
   * Remove progress callback
   */
  offProgress(uploadId: string): void {
    this.progressCallbacks.delete(uploadId);
  }

  /**
   * Send progress update to registered callback
   */
  private updateProgress(uploadId: string, progress: UploadProgress): void {
    const callback = this.progressCallbacks.get(uploadId);
    if (callback) {
      callback(progress);
    }
  }

  /**
   * Validate file before processing
   */
  private async validateFile(file: File, source: string): Promise<{ valid: boolean; errors: string[]; warnings: string[] }> {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic file validation
    if (file.size === 0) {
      errors.push('File appears to be empty');
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      errors.push('File size exceeds 50MB limit');
    }

    // Source-specific validation
    if (source === 'spotify') {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        errors.push('Spotify uploads must be CSV files');
      }
    } else if (source === 'hypeddit') {
      if (!file.name.toLowerCase().endsWith('.html') && !file.name.toLowerCase().endsWith('.htm')) {
        errors.push('Hypeddit uploads must be HTML files');
      }
    }

    return { valid: errors.length === 0, errors, warnings };
  }

  /**
   * Create upload record in database
   */
  private async createUploadRecord(request: UploadRequest): Promise<string> {
    // Try to associate the upload with the signed-in user if available
    let userId: string | null = null;
    try {
      const session = await (supabase as any).auth.getUser?.();
      userId = session?.data?.user?.id || null;
    } catch {}

    const uploadData = {
      source: request.source,
      upload_type: request.source === 'spotify' ? 'csv' : 'html',
      filename: request.file.name,
      file_size: request.file.size,
      artist_id: request.artist_id || null,
      campaign_id: request.campaign_id || null,
      status: 'uploading' as const,
      records_processed: 0,
      duplicates_found: 0,
      new_records: 0,
      uploaded_by: userId,
      created_at: new Date().toISOString()
    };

    const [error, result] = await noderr(
      supabase.from('data_uploads').insert(uploadData).select('id').single()
    );

    if (error) {
      throw new Error(`Failed to create upload record: ${error.message}`);
    }

    return (result as any).data.id;
  }

  /**
   * Update upload record status
   */
  private async updateUploadRecord(
    uploadId: string, 
    updates: Partial<DataUpload>
  ): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('data_uploads')
        .update({
          ...updates,
          processed_at: updates.status === 'completed' ? new Date().toISOString() : undefined
        })
        .eq('id', uploadId)
    );

    if (error) {
      console.error('Failed to update upload record:', error);
    }
  }

  /**
   * Store parsed Spotify data
   */
  private async storeSpotifyData(
    uploadId: string, 
    parsedData: ParsedSpotifyData,
    artist_id?: string
  ): Promise<{ newRecords: number; duplicates: number }> {
    let totalNew = 0;
    let totalDuplicates = 0;

    // Process songs
    if (parsedData.songs.length > 0) {
      const { newRecords, duplicateCount } = await deduplicationService.processBatch(
        parsedData.songs.map(song => ({ ...song, upload_id: uploadId, artist_id })),
        'spotify',
        'csv',
        'song'
      );

      if (newRecords.length > 0) {
        const [error] = await noderr(
          supabase.from('spotify_song_metrics').insert(newRecords)
        );

        if (error) {
          throw new Error(`Failed to store song metrics: ${error.message}`);
        }
      }

      totalNew += newRecords.length;
      totalDuplicates += duplicateCount;
    }

    // Process playlists
    if (parsedData.playlists.length > 0) {
      const { newRecords, duplicateCount } = await deduplicationService.processBatch(
        parsedData.playlists.map(playlist => ({ ...playlist, upload_id: uploadId, artist_id })),
        'spotify',
        'csv',
        'playlist'
      );

      if (newRecords.length > 0) {
        const [error] = await noderr(
          supabase.from('spotify_playlist_metrics').insert(newRecords)
        );

        if (error) {
          throw new Error(`Failed to store playlist metrics: ${error.message}`);
        }
      }

      totalNew += newRecords.length;
      totalDuplicates += duplicateCount;
    }

    return { newRecords: totalNew, duplicates: totalDuplicates };
  }

  /**
   * Store parsed Hypeddit data
   */
  private async storeHypedditData(
    uploadId: string, 
    parsedData: ParsedHypedditData,
    artist_id?: string
  ): Promise<{ newRecords: number; duplicates: number }> {
    let totalNew = 0;
    let totalDuplicates = 0;

    // Process campaigns
    if (parsedData.campaigns.length > 0) {
      const { newRecords, duplicateCount } = await deduplicationService.processBatch(
        parsedData.campaigns.map(campaign => ({ ...campaign, upload_id: uploadId, artist_id })),
        'hypeddit',
        'html',
        'campaign'
      );

      if (newRecords.length > 0) {
        const [error] = await noderr(
          supabase.from('hypeddit_campaign_metrics').insert(newRecords)
        );

        if (error) {
          throw new Error(`Failed to store campaign metrics: ${error.message}`);
        }
      }

      totalNew += newRecords.length;
      totalDuplicates += duplicateCount;
    }

    // Process smart links
    if (parsedData.smartlinks.length > 0) {
      const { newRecords, duplicateCount } = await deduplicationService.processBatch(
        parsedData.smartlinks.map(smartlink => ({ ...smartlink, upload_id: uploadId, artist_id })),
        'hypeddit',
        'html',
        'smartlink'
      );

      if (newRecords.length > 0) {
        const [error] = await noderr(
          supabase.from('hypeddit_smartlink_metrics').insert(newRecords)
        );

        if (error) {
          throw new Error(`Failed to store smartlink metrics: ${error.message}`);
        }
      }

      totalNew += newRecords.length;
      totalDuplicates += duplicateCount;
    }

    return { newRecords: totalNew, duplicates: totalDuplicates };
  }

  /**
   * Main upload processing method
   */
  async processUpload(request: UploadRequest): Promise<UploadResponse> {
    let uploadId: string | undefined;
    console.log('Starting upload process...', { request });

    try {
      // Step 1: Validate file
      console.log('Step 1: Validating file...');
      this.updateProgress(request.client_upload_id || 'temp', {
        stage: 'uploading',
        progress: 10,
        message: 'Validating file...'
      });

      const validation = await this.validateFile(request.file, request.source);
      if (!validation.valid) {
        return {
          success: false,
          message: `File validation failed: ${validation.errors.join(', ')}`
        };
      }

      // Step 2: Create upload record
      console.log('Step 2: Creating upload record...');
      uploadId = await this.createUploadRecord(request);
      console.log(`Upload record created with ID: ${uploadId}`);
      
      this.updateProgress(request.client_upload_id || uploadId, {
        stage: 'uploading',
        progress: 20,
        message: 'Reading file...'
      });

      // Step 3: Read file content
      console.log('Step 3: Reading file content...');
      const fileContent = await request.file.text();
      console.log(`File content read (${fileContent.length} chars)`);
      
      await this.updateUploadRecord(uploadId, {
        raw_data: fileContent,
        status: 'parsing'
      });

      this.updateProgress(request.client_upload_id || uploadId, {
        stage: 'parsing',
        progress: 40,
        message: 'Parsing data...'
      });

      // Step 4: Parse based on source
      console.log('Step 4: Parsing data...');
      let parsedData: ParsedSpotifyData | ParsedHypedditData;
      let totalRecords = 0;
      
      if (request.source === 'spotify') {
        parsedData = await spotifyParserService.parseSpotifyCSV(
          fileContent,
          request.file.name,
          request.artist_id,
          request.mapping?.spotify
        );
        // Apply period override for aggregated CSVs
        if (request.override_period && (request.override_period.dataset === 'songs' || request.override_period.dataset === 'playlists')) {
          const end = request.override_period.end_date;
          const days = request.override_period.days;
          const startDate = new Date(end);
          startDate.setDate(startDate.getDate() - (days - 1));
          (parsedData as any).date_range = { start: startDate.toISOString().split('T')[0], end };
          if ((parsedData as any).songs?.length && request.override_period.dataset === 'songs') {
            (parsedData as any).songs = (parsedData as any).songs.map((r: any) => ({ ...r, recorded_at: end }));
          }
          if ((parsedData as any).playlists?.length && request.override_period.dataset === 'playlists') {
            (parsedData as any).playlists = (parsedData as any).playlists.map((r: any) => ({ ...r, recorded_at: end }));
          }
        }
        // Apply release-date filter for timeline CSVs
        if (request.override_timeline_release_date && (parsedData as any).songs?.length && !(parsedData as any).playlists?.length) {
          const rdate = new Date(request.override_timeline_release_date);
          const cutoff = new Date(rdate.getTime() - 24*60*60*1000);
          const filtered = (parsedData as any).songs.filter((s: any) => new Date(s.recorded_at) >= cutoff);
          (parsedData as any).songs = filtered;
          if (filtered.length) {
            const start = filtered.map((s: any)=>s.recorded_at).sort()[0];
            const end = filtered.map((s: any)=>s.recorded_at).sort().slice(-1)[0];
            (parsedData as any).date_range = { start, end };
          }
        }
        totalRecords = (parsedData as ParsedSpotifyData).songs.length + 
                      (parsedData as ParsedSpotifyData).playlists.length;
        console.log(`Parsed ${totalRecords} Spotify records.`);
      } else if (request.source === 'hypeddit') {
        parsedData = await hypedditParserService.parseHypedditHTML(
          fileContent, 
          request.file.name, 
          request.artist_id
        );
        totalRecords = (parsedData as ParsedHypedditData).campaigns.length + 
                      (parsedData as ParsedHypedditData).smartlinks.length;
        console.log(`Parsed ${totalRecords} Hypeddit records.`);
      } else {
        throw new Error(`Unsupported source: ${request.source}`);
      }

      await this.updateUploadRecord(uploadId, {
        parsed_data: parsedData,
        period_start: parsedData.date_range.start,
        period_end: parsedData.date_range.end,
        records_processed: totalRecords
      });

      this.updateProgress(request.client_upload_id || uploadId, {
        stage: 'deduplicating',
        progress: 60,
        message: 'Checking for duplicates...',
        total_records: totalRecords
      });

      // Step 5: Store data with deduplication
      console.log('Step 5: Storing data with deduplication...');
      let storeResult: { newRecords: number; duplicates: number };
      
      if (request.source === 'spotify') {
        storeResult = await this.storeSpotifyData(
          uploadId, 
          parsedData as ParsedSpotifyData, 
          request.artist_id
        );
      } else {
        storeResult = await this.storeHypedditData(
          uploadId, 
          parsedData as ParsedHypedditData, 
          request.artist_id
        );
      }
      console.log('Data stored:', { storeResult });

      this.updateProgress(request.client_upload_id || uploadId, {
        stage: 'saving',
        progress: 80,
        message: 'Saving data...',
        processed_records: totalRecords,
        new_records: storeResult.newRecords,
        duplicate_records: storeResult.duplicates
      });

      // Step 6: Complete upload
      console.log('Step 6: Completing upload...');
      await this.updateUploadRecord(uploadId, {
        status: 'completed',
        new_records: storeResult.newRecords,
        duplicates_found: storeResult.duplicates
      });

      this.updateProgress(request.client_upload_id || uploadId, {
        stage: 'completed',
        progress: 100,
        message: 'Upload completed successfully!',
        processed_records: totalRecords,
        new_records: storeResult.newRecords,
        duplicate_records: storeResult.duplicates
      });

      // Clean up progress callback
      console.log('Upload process completed successfully.');
      this.offProgress(request.client_upload_id || uploadId);

      return {
        success: true,
        upload_id: uploadId,
        message: `Successfully processed ${storeResult.newRecords} new records (${storeResult.duplicates} duplicates skipped)`,
        preview_data: {
          records_count: totalRecords,
          sample_records: this.getSampleRecords(parsedData),
          date_range: parsedData.date_range
        }
      };

    } catch (error) {
      // Handle errors
      console.error('Error during upload process:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      if (uploadId) {
        await this.updateUploadRecord(uploadId, {
          status: 'failed',
          error_message: errorMessage
        });

        this.updateProgress(request.client_upload_id || uploadId, {
          stage: 'error',
          progress: 0,
          message: errorMessage,
          error_details: errorMessage
        });

        this.offProgress(request.client_upload_id || uploadId);
      }

      return {
        success: false,
        upload_id: uploadId,
        message: errorMessage
      };
    }
  }

  /**
   * Get sample records for preview
   */
  private getSampleRecords(parsedData: ParsedSpotifyData | ParsedHypedditData): any[] {
    const samples: any[] = [];
    
    if ('songs' in parsedData) {
      // Spotify data
      samples.push(...parsedData.songs.slice(0, 3));
      samples.push(...parsedData.playlists.slice(0, 2));
    } else {
      // Hypeddit data
      samples.push(...parsedData.campaigns.slice(0, 3));
      samples.push(...parsedData.smartlinks.slice(0, 2));
    }

    return samples.slice(0, 5); // Limit to 5 samples
  }

  /**
   * Get upload status
   */
  async getUploadStatus(uploadId: string): Promise<DataUpload | null> {
    try {
      const [error, result] = await noderr(
        supabase
          .from('data_uploads')
          .select('*')
          .eq('id', uploadId)
          .single()
      );

      if (error) {
        return null;
      }

      return (result as any).data as DataUpload;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get upload history for user
   */
  async getUploadHistory(limit: number = 20): Promise<DataUpload[]> {
    try {
      const [error, result] = await noderr(
        supabase
          .from('data_uploads')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit)
      );

      if (error) {
        return [];
      }

      return ((result as any)?.data || []) as DataUpload[];
    } catch (error) {
      return [];
    }
  }

  /**
   * Delete upload and associated data
   */
  async deleteUpload(uploadId: string): Promise<boolean> {
    try {
      // Delete associated metrics (cascade should handle this, but being explicit)
      await Promise.all([
        supabase.from('spotify_song_metrics').delete().eq('upload_id', uploadId),
        supabase.from('spotify_playlist_metrics').delete().eq('upload_id', uploadId),
        supabase.from('hypeddit_campaign_metrics').delete().eq('upload_id', uploadId),
        supabase.from('hypeddit_smartlink_metrics').delete().eq('upload_id', uploadId)
      ]);

      // Delete upload record
      const [error] = await noderr(
        supabase.from('data_uploads').delete().eq('id', uploadId)
      );

      return !error;
    } catch (error) {
      console.error('Delete upload error:', error);
      return false;
    }
  }
}

export const uploadProcessorService = new UploadProcessorService();
